import Emitter from '$lib/services/streaming/eventsDispatcher';
import Events from '$lib/services/streaming/eventsDispatcher/events';
import { streamingStats } from '$lib/services/streaming/statistics/sendStats';
import { logWebRTCMetrics } from '$lib/services/streaming/statistics/webRtcMetricsCollector';
import type { EventCallback, RTCPeerConnectionExtended } from '$lib/services/streaming/types';

import type { StreamingStats } from '@api/types';
import { streamingLoggerStore } from '@components/room/streaming-debugger/stores/logger';
import { LogType } from '@shared/types';
import { StreamingType, StreamProvider } from '@shared/types/room';

// const cloudflareLiveInputIdRx = /(\w{32})(\/webRTC\/publish|\/webRTC\/play)$/;

export const StreamingStatsEvents = {
    rtc_default: 'rtc_default',

    /* connectionstatechange */
    rtc_state_new: 'rtc_state_new',
    rtc_state_connecting: 'rtc_state_connecting',
    rtc_state_connected: 'rtc_state_connected',
    rtc_state_disconnected: 'rtc_state_disconnected',
    rtc_state_failed: 'rtc_state_failed',
    rtc_state_closed: 'rtc_closed',

    /* icegatheringstatechange */
    rtc_gathering_new: 'rtc_gathering_new',
    rtc_gathering_gathering: 'rtc_gathering_gathering',
    rtc_gathering_complete: 'rtc_gathering_complete',

    /* negotiationneeded */
    rtc_negotiationneeded: 'negotiationneeded',

    /* icecandidate */
    rtc_icecandidate: 'icecandidate',

    /* icecandidateerror */
    rtc_icecandidateerror: 'icecandidateerror',

    /* iceconnectionstatechange */
    rtc_ice_new: 'rtc_ice_new',
    rtc_ice_checking: 'rtc_ice_checking',
    rtc_ice_connected: 'rtc_ice_connected',
    rtc_ice_completed: 'rtc_ice_completed',
    rtc_ice_failed: 'rtc_ice_failed',
    rtc_ice_disconnected: 'rtc_ice_disconnected',
    rtc_ice_closed: 'rtc_ice_closed',

    /* signalingstatechange */
    rtc_signaling_stable: 'rtc_signaling_stable',
    rtc_signaling_have_local_offer: 'rtc_signaling_have_local_offer',
    rtc_signaling_have_remote_offer: 'rtc_signaling_have_remote_offer',
    rtc_signaling_have_local_pranswer: 'rtc_signaling_have_local_pranswer',
    rtc_signaling_have_remote_pranswer: 'rtc_signaling_have_remote_pranswer',
    rtc_signaling_closed: 'rtc_signaling_closed',

    // Отдельные ивенты, не подписочные
    rtc_reconnection_failed: 'rtc_reconnection_failed',
    rtc_reconnect_needed: 'rtc_reconnect_needed',
    rtc_connection_failed: 'rtc_reconnect_failed',
    rtc_connection_error: 'rtc_connect_error',
    rtc_no_media: 'rtc_no_media',
    rtc_no_media_element: 'rtc_no_media_element',
    rtc_on_track: 'rtc_on_track',
    rtc_media_recovered: 'rtc_media_recovered',
    rtc_adapter_connect_error: 'rtc_adapter_connect_error',
    rtc_streaming_error: 'rtc_streaming_error',
    rtc_unload: 'rtc_unload',
    rtc_stop: 'rtc_stop',
    rtc_destroy: 'rtc_destroy',
    rtc_error: 'rtc_error',
    rtc_disconnect_error: 'rtc_disconnect_error',
    rtc_set_remote_description_error: 'rtc_set_remote_description_error',
    rtc_set_local_description_error: 'rtc_set_local_description_error',
    rtc_request_offer_error: 'rtc_request_offer_error',
    rtc_send_answer_no_peer_connection: 'rtc_send_answer_no_peer_connection',
    rtc_send_answer_error: 'rtc_send_answer_error',
    rtc_send_offer_error: 'rtc_send_offer_error',
    rtc_send_offer_no_peer_connection: 'rtc_send_offer_no_peer_connection',
    rtc_send_offer_connection_error: 'rtc_send_offer_connection_error',

    rtc_create_offer_error: 'rtc_create_offer_error',
    rtc_ice_gathering_error: 'rtc_ice_gathering_error',
    rtc_gather_candidates_error: 'rtc_gather_candidates_error',
    rtc_post_sdp_offer_error: 'rtc_post_sdp_offer_error',
    rtc_answer_sdp_error: 'rtc_answer_sdp_error',

    stop_delivery: 'stop_delivery',
    stop_broadcast: 'stop_broadcast',

    video_element_error: 'video_element_error',
    video_element_can_play: 'video_element_can_play',
};

type PeerConnectionCallback = {
    event: string;
    callback: () => void;
};

type RTCConnectionIDs = { [id: string]: PeerConnectionCallback[] };

type RTCEvent = {
    [key: string]: unknown;

    type: string;
    target: RTCPeerConnectionExtended;

    errorText?: string;
    candidate?: { type: string };
};

type RTCEventData = {
    signalingState: string;
    streamingType: StreamingType;

    errorText?: string;
    candidate?: string;
}

export class StreamingStatistics {
    private provider: StreamProvider;
    rtcConnectionCallbacks: RTCConnectionIDs;
    deliveryVideoElementOnErrorBinded: CallableFunction | null;
    broadcastVideoElementOnErrorBinded: CallableFunction | null;

    broadcastLastCandidate: string | null;
    deliveryLastCandidate: string | null;
    broadcastConnectionState: string | null;
    deliveryConnectionState: string | null;

    peerConnection: RTCPeerConnectionExtended | null;

    roomStartMs: number | null;
    broadcastStartMs: number | null;
    deliveryStartMs: number | null;

    constructor() {
        this.rtcConnectionCallbacks = {};
        this.roomStartMs = null;
        this.broadcastStartMs = null;
        this.deliveryStartMs = null;

        this.deliveryVideoElementOnErrorBinded = null;
        this.broadcastVideoElementOnErrorBinded = null;

        this.broadcastLastCandidate = null;
        this.deliveryLastCandidate = null;

        this.broadcastConnectionState = null;
        this.deliveryConnectionState = null;

        this.peerConnection = null;
        this.provider = StreamProvider.Unknown;

        this.init();
    }

    init() {
        this.attachEvents();
    }

    public setStreamProvider(provider: StreamProvider) {
        this.provider = provider;
    }

    /* eslint-disable @typescript-eslint/ban-ts-comment */
    attachEvents() {
        Emitter.on(
            Events.STREAMING_STATS_WEBRTC_WATCH_PEER_CONNECTION,
            this.watchPeerConnection.bind(this) as EventCallback
        );

        Emitter.on(
            Events.STREAMING_STATS_WEBRTC_UNWATCH_PEER_CONNECTION,
            this.detachWebRTCPeerEvents.bind(this) as EventCallback
        );

        Emitter.on(
            Events.STREAMING_STATS_ATTACH_VIDEO_ON_ERROR,
            this.attachVideoElementErrorCallback.bind(this) as EventCallback
        );

        Emitter.on(
            Events.STREAMING_STATS_DETACH_VIDEO_ON_ERROR,
            this.detachVideoElementErrorCallback.bind(this) as EventCallback
        );

        Emitter.on(Events.STREAMING_STATS_SET_ROOM_START_TIMER,
            this.setRoomStartTimer.bind(this) as EventCallback);
        Emitter.on(Events.STREAMING_STATS_SET_DELIVERY_START_TIMER,
            this.setDeliveryStartTimer.bind(this) as EventCallback);
        Emitter.on(Events.STREAMING_STATS_SET_BROADCAST_START_TIMER,
            this.setBroadcastStartTimer.bind(this) as EventCallback);

        Emitter.on(Events.STREAMING_STATS_WEBRTC_STATE_UPDATE, this.sendStats.bind(this) as EventCallback);
        Emitter.on(Events.STREAMING_STATS_WEBRTC_SEND_EVENT, this.sendStats.bind(this) as EventCallback);

        Emitter.on(Events.STREAMING_STATS_VIDEO_DELIVERY_CAN_PLAY,
            this.sendVideoDeliveryCanPlay.bind(this) as EventCallback);
    }
    /* eslint-enable @typescript-eslint/ban-ts-comment */

    watchPeerConnection(peerConnection: RTCPeerConnectionExtended) {
        if (!(peerConnection instanceof RTCPeerConnection)) {
            return;
        }

        this.attachWebRTCPeerEvents(peerConnection);
    }

    attachWebRTCPeerEvents(peerConnection: RTCPeerConnectionExtended) {
        this.peerConnection = peerConnection;
        this.rtcConnectionCallbacks[peerConnection.connectionId] = [];

        const callbacksData = this.rtcConnectionCallbacks[peerConnection.connectionId];

        const stateCallback = this.onConnectionStateChange.bind(this);
        const gatheringCallback = this.onGatheringStateChange.bind(this);
        const onNegotiationNeeded = this.onNegotiationNeeded.bind(this);
        const iceCallback = this.onIceConnectionStateChange.bind(this);
        const signalingCallback = this.onSignalingStateChange.bind(this);
        const onIceCandidateError = this.onIceCandidateError.bind(this);
        const onIceCandidate = this.onIceCandidate.bind(this);

        const eventsInfo: [keyof RTCPeerConnectionEventMap, () => void][] = [
            ['connectionstatechange', stateCallback],
            ['icegatheringstatechange', gatheringCallback],
            ['negotiationneeded', onNegotiationNeeded],
            ['icecandidateerror', onIceCandidateError],
            ['icecandidate', onIceCandidate],
            ['iceconnectionstatechange', iceCallback],
            ['signalingstatechange', signalingCallback],
        ];

        eventsInfo.forEach((info) => {
            const [event, callback] = info;

            callbacksData.push({ event, callback });
            peerConnection.addEventListener(event, callback);
        });
    }

    detachWebRTCPeerEvents(peerConnection: RTCPeerConnectionExtended) {
        this.peerConnection = null;
        const callbacks = this.rtcConnectionCallbacks[peerConnection.connectionId];

        callbacks?.forEach(({ event, callback }) => {
            peerConnection.removeEventListener(event, callback);
        });
    }

    onGatheringStateChange(event?: RTCEvent) {
        if (!event?.target) {
            return;
        }

        const { data, peerConnection, defaultState } = this.getPeerExtInfo(event);

        const events = {
            new: StreamingStatsEvents.rtc_gathering_new,
            gathering: StreamingStatsEvents.rtc_gathering_gathering,
            complete: StreamingStatsEvents.rtc_gathering_complete,
        };

        this.sendStats(events[peerConnection.iceGatheringState] || defaultState, data);
    }

    onIceCandidate(event?: RTCEvent) {
        const type = event?.candidate?.type;

        if (!type || !event?.target) {
            return;
        }

        const { data } = this.getPeerExtInfo(event);

        if (data.streamingType === StreamingType.Broadcast) {
            this.broadcastLastCandidate = type;
        } else if (data.streamingType === StreamingType.Delivery) {
            this.deliveryLastCandidate = type;
        } else {
            return;
        }
    }

    onNegotiationNeeded(event?: RTCEvent) {
        if (!event?.target) {
            return;
        }

        const { data } = this.getPeerExtInfo(event);

        if (data.streamingType === StreamingType.Broadcast) {
            this.setBroadcastStartTimer();
        }

        this.sendStats(event.type, data);
    }

    onIceCandidateError(event?: RTCEvent) {
        if (!event?.target) {
            return;
        }

        const { data } = this.getPeerExtInfo(event);

        data.errorText = event.errorText;

        this.sendStats(event.type, data);
    }

    onIceConnectionStateChange(event?: RTCEvent) {
        if (!event?.target) {
            return;
        }

        const { data, peerConnection, defaultState } = this.getPeerExtInfo(event);
        const events = {
            new: StreamingStatsEvents.rtc_ice_new,
            checking: StreamingStatsEvents.rtc_ice_checking,
            connected: StreamingStatsEvents.rtc_ice_connected,
            completed: StreamingStatsEvents.rtc_ice_completed,
            disconnected: StreamingStatsEvents.rtc_ice_disconnected,
            closed: StreamingStatsEvents.rtc_ice_closed,
            failed: StreamingStatsEvents.rtc_ice_failed,
        };

        this.sendStats(events[peerConnection.iceConnectionState] || defaultState, data);
    }
    onSignalingStateChange(event?: RTCEvent) {
        if (!event?.target) {
            return;
        }

        const { data, peerConnection, defaultState } = this.getPeerExtInfo(event);
        const events = {
            stable: StreamingStatsEvents.rtc_signaling_stable,
            'have-local-offer': StreamingStatsEvents.rtc_signaling_have_local_offer,
            'have-remote-offer': StreamingStatsEvents.rtc_signaling_have_remote_offer,
            'have-local-pranswer': StreamingStatsEvents.rtc_signaling_have_local_pranswer,
            'have-remote-pranswer': StreamingStatsEvents.rtc_signaling_have_remote_pranswer,
            closed: StreamingStatsEvents.rtc_signaling_closed,
        };

        this.sendStats(events[peerConnection.signalingState] || defaultState, data);
    }

    onConnectionStateChange(event?: RTCEvent) {
        if (!event?.target) {
            return;
        }

        const { data, peerConnection, defaultState } = this.getPeerExtInfo(event);
        const state = peerConnection.connectionState;

        if (data.streamingType === StreamingType.Broadcast) {
            this.broadcastConnectionState = state.toString();
        } else {
            this.deliveryConnectionState = state.toString();
        }

        const events = {
            new: StreamingStatsEvents.rtc_state_new,
            connecting: StreamingStatsEvents.rtc_state_connecting,
            connected: StreamingStatsEvents.rtc_state_connected,
            disconnected: StreamingStatsEvents.rtc_state_disconnected,
            failed: StreamingStatsEvents.rtc_state_failed,
            closed: StreamingStatsEvents.rtc_state_closed,
        };

        this.sendStats(events[state] || defaultState, data);

        if (['disconnected', 'closed', 'failed'].includes(events[state])) {
            this.detachWebRTCPeerEvents(peerConnection);
        }
    }

    attachVideoElementErrorCallback(videoElement: HTMLVideoElement, streamingType: StreamingType) {
        let callback;

        if (streamingType === StreamingType.Broadcast && !this.broadcastVideoElementOnErrorBinded) {
            this.broadcastVideoElementOnErrorBinded = this.onVideoElementError.bind(this, videoElement, streamingType);

            callback = this.broadcastVideoElementOnErrorBinded;
        } else if (streamingType === StreamingType.Delivery && !this.deliveryVideoElementOnErrorBinded) {
            this.deliveryVideoElementOnErrorBinded = this.onVideoElementError.bind(this, videoElement, streamingType);

            callback = this.deliveryVideoElementOnErrorBinded;
        } else {
            return;
        }

        videoElement?.addEventListener('error', callback as EventListenerOrEventListenerObject);
    }

    detachVideoElementErrorCallback(videoElement: HTMLVideoElement, streamingType: StreamingType) {
        const callback = streamingType === StreamingType.Broadcast
            ? this.broadcastVideoElementOnErrorBinded
            : this.deliveryVideoElementOnErrorBinded;

        videoElement?.removeEventListener('error', callback as EventListenerOrEventListenerObject);
    }

    onVideoElementError(videoElement: HTMLVideoElement, streamingType: StreamingType) {
        const errorCode = videoElement?.error?.code;

        let message = 'UNKNOWN_ERROR';

        switch (errorCode) {
            case 1:
                message = 'MEDIA_ERR_ABORTED';
                break;
            case 2:
                message = 'MEDIA_ERR_NETWORK';
                break;
            case 3:
                message = 'MEDIA_ERR_DECODE';
                break;
            case 4:
                message = 'MEDIA_ERR_SRC_NOT_SUPPORTED';
                break;
        }

        this.sendStats(StreamingStatsEvents.video_element_error, { streamingType, errorText: message });
    }

    getPeerConnectionExtendedStats() {
        if (!this.peerConnection) {
            return undefined;
        }
        return logWebRTCMetrics(this.peerConnection);
    }

    async getExtendedStatsForLogger(extraData: { [key: string]: unknown }) {
        const result: { [key: string]: unknown } = {};
        const peerExtendedStats = await this.getPeerConnectionExtendedStats();

        result.iceCandidate = extraData.iceCandidate;
        result.signalingState = extraData.signalingState;

        if (extraData.errorText) {
            result.errorText = extraData.errorText;
        }

        if (peerExtendedStats) {
            result.connection = peerExtendedStats.connection;
            result.video = peerExtendedStats.video;
            result.audio = peerExtendedStats.audio;
        }

        return result;
    }

    getPeerExtInfo(event: RTCEvent) {
        const peerConnection = event.target;
        const streamingType = peerConnection.streamingType;
        const defaultState = StreamingStatsEvents.rtc_default;
        const data: RTCEventData = { streamingType, signalingState: peerConnection.signalingState };

        return { data, peerConnection, defaultState };
    }

    setRoomStartTimer() {
        this.roomStartMs = Date.now();
    }

    setDeliveryStartTimer() {
        this.deliveryStartMs = Date.now();
    }

    setBroadcastStartTimer() {
        this.broadcastStartMs = Date.now();
    }

    sendVideoDeliveryCanPlay() {
        this.sendStats(
            StreamingStatsEvents.video_element_can_play,
            {
                streamingType: StreamingType.Delivery,
            }
        );
    }

    async sendStats(event: string, data: { [key: string]: unknown }) {
        let ms = null;

        if (data?.streamingType === StreamingType.Broadcast) {
            if (typeof this.broadcastStartMs === 'number') {
                ms = Math.max(0, Date.now() - this.broadcastStartMs);
            }
        } else if (data?.streamingType === StreamingType.Delivery) {
            if (typeof this.deliveryStartMs === 'number') {
                ms = Math.max(0, Date.now() - this.deliveryStartMs);
            }
        } else if (typeof this.roomStartMs === 'number') {
            ms = Math.max(0, Date.now() - this.roomStartMs);
        }

        const extraData = data;

        if (extraData?.streamingType === StreamingType.Broadcast) {
            extraData.iceCandidate = this.broadcastLastCandidate;
            extraData.peerConnectionState = this.broadcastConnectionState;
        } else if (extraData?.streamingType === StreamingType.Delivery) {
            extraData.iceCandidate = this.deliveryLastCandidate;
            extraData.peerConnectionState = this.deliveryConnectionState;
        }

        const eventData: StreamingStats = {
            log: 'streaming_stats',
            event,
            extraData,
            ms,
            provider: this.provider,
        };

        const extendedStats = await this.getExtendedStatsForLogger(extraData);

        if (extraData?.streamingType === 'broadcast') {
            streamingLoggerStore.log(
                {
                    type: LogType.WebRTCBroadcast,
                    message: event,
                },
                { ms, ...extendedStats }
            );
        }

        if (extraData?.streamingType === 'delivery') {
            streamingLoggerStore.log(
                {
                    type: LogType.WebRTCDelivery,
                    message: event,
                },
                { ms, ...extendedStats },

                {
                    includeExtendedData: ['signalingState', 'iceCandidate', 'errorText'],
                }
            );
        }

        streamingStats.addToBatch(eventData);
    }
}
